// src/pages/TeacherDetail.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, getDocs, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../services/firebase";
import { FiX } from "react-icons/fi";
import { getInitials } from "../utils/commonUtils";
import "./TeacherDetail.css";

const TeacherDetail = ({ setNavbarVisibility }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [schoolNames, setSchoolNames] = useState({});
    const [classrooms, setClassrooms] = useState([]);
    const [assignedClassrooms, setAssignedClassrooms] = useState([]);
    const [user, setUser] = useState(null);
    const teacher = location.state?.teacher;

    useEffect(() => {
        setNavbarVisibility(false); // hide navbar
        return () => setNavbarVisibility(true); // show back on unmount
    }, [setNavbarVisibility]);

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("user"));
        setUser(storedUser);
    }, []);

    useEffect(() => {
        const fetchSchoolNames = async () => {
            if (!teacher?.schools) return;

            const results = {};
            for (const school of teacher.schools) {
                try {
                    const ref = doc(db, "schools", school.schoolId);
                    const snap = await getDoc(ref);
                    if (snap.exists()) {
                        results[school.schoolId] = snap.data().name;
                    } else {
                        results[school.schoolId] = "Nama tidak dijumpai";
                    }
                } catch (error) {
                    console.error("Gagal ambil nama sekolah:", error);
                    results[school.schoolId] = "Ralat";
                }
            }
            setSchoolNames(results);
        };

        fetchSchoolNames();
    }, [teacher]);

    useEffect(() => {
        const fetchClassrooms = async () => {
            if (!teacher?.schools) return;
            let allClassrooms = [];
            let assigned = [];

            for (const school of teacher.schools) {
                const classroomsRef = collection(db, "schools", school.schoolId, "classrooms");
                const snap = await getDocs(classroomsRef);
                const classroomsList = snap.docs.map(doc => {
                    const data = doc.data();
                    const isAssigned = Array.isArray(data.teachers) && data.teachers.includes(teacher.id);
                    if (isAssigned) {
                        assigned.push({
                            id: doc.id,
                            schoolId: school.schoolId,
                            ...data
                        });
                    }

                    return {
                        id: doc.id,
                        schoolId: school.schoolId,
                        ...data
                    };
                });

                allClassrooms = [...allClassrooms, ...classroomsList];
            }

            setClassrooms(allClassrooms);
            setAssignedClassrooms(assigned); // ✅ set kelas yang dia mengajar
        };

        fetchClassrooms();
    }, [teacher]);


    const assignTeacherToClassroom = async (schoolId, classroomId, teacherId) => {
        const ref = doc(db, "schools", schoolId, "classrooms", classroomId);
        await updateDoc(ref, {
            teachers: arrayUnion(teacherId)
        });

        const updatedClassroom = classrooms.find(c => c.id === classroomId && c.schoolId === schoolId);
        if (updatedClassroom && !assignedClassrooms.find(c => c.id === classroomId)) {
            setAssignedClassrooms(prev => [...prev, updatedClassroom]);
        }

        setClassrooms(prev =>
            prev.map(cls =>
                cls.id === classroomId && cls.schoolId === schoolId
                    ? {
                        ...cls,
                        teachers: [...(cls.teachers || []), teacherId],
                    }
                    : cls
            )
        );

    };

    const removeTeacherFromClassroom = async (schoolId, classroomId, teacherId) => {
        const ref = doc(db, "schools", schoolId, "classrooms", classroomId);
        await updateDoc(ref, {
            teachers: arrayRemove(teacherId)
        });

        setAssignedClassrooms(prev => prev.filter(c => c.id !== classroomId));

        setClassrooms(prev =>
            prev.map(cls =>
                cls.id === classroomId && cls.schoolId === schoolId
                    ? {
                        ...cls,
                        teachers: (cls.teachers || []).filter(id => id !== teacherId),
                    }
                    : cls
            )
        );

    };


    if (!teacher) {
        return <p>Guru tidak dijumpai.</p>;
    }

    return (
        <div className="teacher-detail-container">


            <div className="teacher-detail-inside">
                <div className="teacher-avatar-big">
                    {teacher.avatar ? (
                        <img src={teacher.avatar} alt="Avatar Guru" />
                    ) : (
                        <div >{getInitials(teacher.name)}</div>
                    )}
                </div>

                <h2 className="teacher-name">{teacher.name}</h2>
                <p className="teacher-email">{teacher.email}</p>

                <div className="teacher-schools">
                    <h4>Sekolah:</h4>
                    <ul>
                        {(teacher.schools || []).map((s, i) => (
                            <li key={i}>
                                {schoolNames[s.schoolId] || s.schoolId}{" "}
                                {s.isAdmin && <strong> - Admin</strong>}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="teacher-classes">
                    <h4>Kelas:</h4>
                    <ul>
                        {classrooms.map((cls) => {
                            const isOwner = cls.teacherId === teacher.id;
                            const isAssigned = Array.isArray(cls.teachers) && cls.teachers.includes(teacher.id);

                            const canModify = user?.schools?.some(
                                s => s.schoolId === cls.schoolId && s.isAdmin
                            ); // Admin sekolah yang sedang login

                            return (
                                <li key={cls.id}>
                                    {cls.name}

                                    {isOwner ? (
                                        <span style={{ color: "blue" }}>Admin</span>
                                    ) : isAssigned ? (
                                        <>
                                            <span style={{ color: "green", marginRight: 8 }}>Guru</span>
                                            {canModify && (
                                                <button onClick={() => removeTeacherFromClassroom(cls.schoolId, cls.id, teacher.id)}>
                                                    Buang
                                                </button>
                                            )}
                                        </>
                                    ) : (
                                        canModify && (
                                            <button onClick={() => assignTeacherToClassroom(cls.schoolId, cls.id, teacher.id)}>
                                                Tambah
                                            </button>
                                        )
                                    )}
                                </li>
                            );
                        })}



                    </ul>
                </div>
            </div>


            <div className="toggle-icon" onClick={() => navigate(-1)}>


                <FiX className="icon-button" />
                Tutup


            </div>


        </div>
    );
};

export default TeacherDetail;
